
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ChevronRight, Grid3x3 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

async function fetchCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .eq('is_active', true)
    .neq('slug', 'uncategorized')
    .order('display_order', { ascending: true });

  if (error) throw new Error(error.message);
  return data || [];
}

export function CategoryNavigation({ onCategorySelect, currentCategory = 'all' }) {
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
  });

  const handleCategoryClick = (categorySlug) => {
    if (onCategorySelect) {
      onCategorySelect(categorySlug);
    }
  };

  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto scrollbar-hide">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex-shrink-0 w-32 h-20 bg-slate-200 dark:bg-dark-tertiary rounded-xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Category Grid */}
      <div className="flex gap-4 pb-6 p-4 -mt-4 -ml-4 overflow-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-blue-500 scrollbar-track-transparent">
        {/* All Categories Option */}
        <motion.button
          onClick={() => handleCategoryClick('all')}
          className={`flex-shrink-0 w-32 relative group p-4 rounded-xl border transition-all duration-300 hover:scale-105 ${
            currentCategory === 'all'
              ? 'line-bg bg-gradient-to-br from-blue-500 to-purple-600 border-blue-500 text-white shadow-lg'
              : 'bg-white/80 dark:bg-dark-card/80 border-slate-200 dark:border-dark-primary hover:border-blue-300 dark:hover:border-blue-500 backdrop-blur-sm'
          }`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="flex flex-col items-center space-y-2">
            <div className={`p-2 rounded-lg ${currentCategory === 'all' ? 'bg-white/20' : 'bg-slate-100 dark:bg-dark-tertiary'}`}>
              <Grid3x3 className={`w-6 h-6 ${currentCategory === 'all' ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`} />
            </div>
            <span className={`text-sm font-medium ${currentCategory === 'all' ? 'text-white' : 'text-slate-800 dark:text-white'}`}>
              All Categories
            </span>
          </div>
        </motion.button>

        {/* Dynamic Categories */}
        {categories.map((category, index) => (
          <motion.button
            key={category.slug}
            onClick={() => handleCategoryClick(category.slug)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            className={`flex-shrink-0 w-32 relative group p-4 rounded-xl border transition-all duration-300 hover:scale-105 ${
              currentCategory === category.slug
                ? 'bg-gradient-to-br from-blue-500 to-purple-600 border-blue-500 text-white shadow-lg'
                : 'bg-white/80 dark:bg-dark-card/80 border-slate-200 dark:border-dark-primary hover:border-blue-300 dark:hover:border-blue-500 backdrop-blur-sm'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className={`p-2 rounded-lg ${currentCategory === category.slug ? 'bg-white/20' : 'bg-slate-100 dark:bg-dark-tertiary'}`}>
                {category.icon ? (
                  <img src={category.icon} alt={category.name} className="w-6 h-6" />
                ) : (
                  <Grid3x3 className={`w-6 h-6 ${currentCategory === category.slug ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`} />
                )}
              </div>
              <span className={`text-sm font-medium text-center ${currentCategory === category.slug ? 'text-white' : 'text-slate-800 dark:text-white'}`}>
                {category.name}
              </span>
              {category.product_count && (
                <span className={`text-xs ${currentCategory === category.slug ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                  {category.product_count} items
                </span>
              )}
            </div>
          </motion.button>
        ))}
      </div>

      {/* Breadcrumb */}
      {currentCategory !== 'all' && (
        <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
          <button
            onClick={() => handleCategoryClick('all')}
            className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            All Categories
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 transition-colors font-medium">
            {categories.find(cat => cat.slug === currentCategory)?.name || currentCategory}
          </span>
        </div>
      )}
    </motion.div>
  );
}
